import React, { FC, useEffect, useMemo } from 'react'

import { Box, Stack, TablePagination, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { EmptyAccountData } from '../../components/EmptyAccountData'
import { EmptyTable } from '../../components/Table/EmptyTable'
import { CommonTableColumnsEnum } from '../../components/Table/types'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { SortDirectionCapital } from '../../types'
import { getTablePaginationDisplayedRows } from '../../utils'

import { DailyStatsCard } from './Card/DailyStatsCard'
import { DailyStatsHeader } from './DailyStatsHeader'
import { fetchDailyStats, updatePage, updatePageSize } from './DailyStatsReducer'

const DailyStatsPage: FC = () => {
  const { spacing } = useTheme()
  const { t } = useTranslation()

  const noTradesMessage: string = t(
    'dailyStatsPage.emptyState.noTrades',
    'There are no trades to display for the selected time. Try to select different filters',
  )

  const {
    dailyStatsData,
    pagination: { page, perPage, itemCount, columns },
  } = useAppSelector((state) => state.dailyStats)

  const { currentAccount, period } = useAppSelector(({ userAccounts }) => userAccounts)
  const [startDate, endDate] = period

  const searchPeriod = useMemo(
    () => (startDate && endDate ? [startDate, endDate] : [null, null]),
    [startDate, endDate],
  )

  const location = useLocation()
  const navigate = useNavigate()
  const fromModal = location.state?.fromModal

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (fromModal) {
      navigate('.', { state: { fromModal: false } })
    } else if (currentAccount?.id) {
      dispatch(
        fetchDailyStats({
          page,
          perPage,
          columns: [CommonTableColumnsEnum.id, CommonTableColumnsEnum.symbol, ...columns],
          period: searchPeriod,
          account: currentAccount.id,
          order: SortDirectionCapital.desc,
        }),
      )
    }
  }, [page, perPage, columns, currentAccount?.id, period])

  const handlePageChange = (e: unknown, newPage: number) => {
    dispatch(updatePage(newPage + 1))
  }
  const handlePageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updatePageSize(parseInt(e.target.value, 10)))
    dispatch(updatePage(1))
  }

  const dailyStatsDataArray = Object.entries(dailyStatsData)

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      {dailyStatsDataArray.length ? <DailyStatsHeader /> : null}

      {!dailyStatsDataArray.length ? (
        currentAccount ? (
          <EmptyTable message={noTradesMessage} />
        ) : (
          <EmptyAccountData />
        )
      ) : (
        <Stack p={spacing(2)} direction="column" flex={1}>
          <Stack direction="column" spacing={2} flexGrow={1}>
            {dailyStatsDataArray.map((dailyStats) => {
              const [date, dailyStatsItem] = dailyStats

              return (
                <DailyStatsCard key={date} date={date} data={dailyStatsItem} columns={columns} />
              )
            })}
          </Stack>

          <TablePagination
            labelRowsPerPage={t('dailyStatsPage.pagination.rowsPerPage', 'Per page')}
            labelDisplayedRows={getTablePaginationDisplayedRows(t)}
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            component="div"
            count={itemCount}
            rowsPerPage={perPage}
            page={page - 1}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handlePageSizeChange}
          />
        </Stack>
      )}
    </Box>
  )
}

export default DailyStatsPage
